import React from 'react';
import { 
  Box, 
  Typography, 
  Card, 
  CardContent, 
  Paper, 
  Grid,
  Container,
  Chip
} from '@mui/material';
import { 
  Rocket, 
  Business, 
  TrendingUp, 
  EmojiEvents, 
  Engineering,
  Groups,
  Public,
  Star
} from '@mui/icons-material';

const Historia = () => {
  const milestones = [
    {
      year: '2014',
      title: 'Fundación de CONEM',
      description: 'Inicio de operaciones con un equipo de 3 ingenieros especializados en automatización industrial.',
      icon: <Rocket />,
      color: 'primary'
    },
    {
      year: '2016',
      title: 'Primera Expansión',
      description: 'Apertura de nueva sede y crecimiento del equipo a 8 profesionales. Primeros proyectos internacionales.',
      icon: <Business />,
      color: 'secondary'
    },
    {
      year: '2018',
      title: 'Certificaciones ISO',
      description: 'Obtención de certificaciones ISO 9001 y ISO 14001. Reconocimiento por calidad y gestión ambiental.',
      icon: <EmojiEvents />,
      color: 'success'
    },
    {
      year: '2020',
      title: 'Transformación Digital',
      description: 'Implementación de tecnologías 4.0 y desarrollo de soluciones IoT para la industria.',
      icon: <Engineering />,
      color: 'info'
    },
    {
      year: '2022',
      title: 'Expansión Regional',
      description: 'Apertura de oficinas en 3 países de Latinoamérica. Equipo de más de 15 profesionales.',
      icon: <Public />,
      color: 'warning'
    },
    {
      year: '2024',
      title: 'Liderazgo en Innovación',
      description: 'Reconocimiento como empresa líder en innovación tecnológica. Más de 100 proyectos exitosos.',
      icon: <Star />,
      color: 'error'
    }
  ];

  const achievements = [
    { number: '10+', label: 'Años de Experiencia', icon: <Business /> },
    { number: '100+', label: 'Proyectos Completados', icon: <EmojiEvents /> },
    { number: '50+', label: 'Clientes Satisfechos', icon: <Groups /> },
    { number: '15+', label: 'Profesionales', icon: <Engineering /> }
  ];

  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      {/* Header */}
      <Box textAlign="center" mb={6}>
        <Typography variant="h3" color="primary" fontWeight="bold" gutterBottom>
          Nuestra Historia
        </Typography>
        <Typography variant="h6" color="text.secondary" sx={{ maxWidth: 700, mx: 'auto' }}>
          Una década de crecimiento, innovación y compromiso con la excelencia
        </Typography>
      </Box>

      {/* Logros destacados */}
      <Grid container spacing={3} mb={6}>
        {achievements.map((achievement, index) => (
          <Grid size={{ xs: 6, md: 3 }} key={index}>
            <Card 
              elevation={6} 
              sx={{ 
                p: 3, 
                textAlign: 'center', 
                borderRadius: 3,
                background: `linear-gradient(135deg, ${index % 2 === 0 ? '#1976d2' : '#388e3c'} 0%, ${index % 2 === 0 ? '#1565c0' : '#2e7d32'} 100%)`,
                color: 'white',
                transition: 'transform 0.3s ease-in-out',
                '&:hover': {
                  transform: 'translateY(-8px)'
                }
              }}
            >
              {achievement.icon && React.cloneElement(achievement.icon, { sx: { fontSize: 40, mb: 2 } })}
              <Typography variant="h4" fontWeight="bold" gutterBottom>
                {achievement.number}
              </Typography>
              <Typography variant="body1" fontWeight="500">
                {achievement.label}
              </Typography>
            </Card>
          </Grid>
        ))}
      </Grid>

      {/* Timeline de la historia */}
      <Paper elevation={6} sx={{ p: 4, borderRadius: 4, mb: 6 }}>
        <Typography variant="h4" color="primary" fontWeight="bold" textAlign="center" gutterBottom>
          Línea de Tiempo
        </Typography>
        <Box sx={{ mt: 4 }}>
          <Grid container spacing={3}>
            {milestones.map((milestone, index) => (
              <Grid size={{ xs: 12, md: 6 }} key={index}>
                <Card 
                  elevation={4} 
                  sx={{ 
                    p: 3, 
                    borderRadius: 3,
                    transition: 'transform 0.3s ease-in-out',
                    '&:hover': {
                      transform: 'translateY(-8px)',
                      boxShadow: 8
                    },
                    height: '100%',
                    boxSizing: 'border-box',
                    display: 'flex',
                    flexDirection: 'column'
                  }}
                >
                  <Box display="flex" alignItems="center" mb={2}>
                    <Box 
                      sx={{ 
                        p: 2, 
                        borderRadius: 2, 
                        bgcolor: `${milestone.color}.main`, 
                        color: 'white',
                        mr: 2
                      }}
                    >
                      {milestone.icon}
                    </Box>
                    <Chip 
                      label={milestone.year} 
                      color={milestone.color} 
                      sx={{ fontWeight: 'bold', fontSize: '1rem' }}
                    />
                  </Box>
                  <Typography variant="h6" fontWeight="bold" color="primary" gutterBottom>
                    {milestone.title}
                  </Typography>
                  <Typography variant="body1" color="text.secondary" sx={{ lineHeight: 1.6, flexGrow: 1 }}>
                    {milestone.description}
                  </Typography>
                </Card>
              </Grid>
            ))}
          </Grid>
        </Box>
      </Paper>

      {/* Valores históricos */}
      <Grid container spacing={4}>
        <Grid size={{ xs: 12, md: 6 }}>
          <Paper 
            elevation={6} 
            sx={{ 
              p: 4, 
              borderRadius: 4, 
              background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
              color: 'white',
              height: '100%', boxSizing: 'border-box',
            }}
          >
            <Typography variant="h5" fontWeight="bold" gutterBottom>
              Nuestro Legado
            </Typography>
            <Typography variant="body1" sx={{ lineHeight: 1.8, mb: 3 }}>
              Desde nuestros inicios en 2014, hemos mantenido un compromiso inquebrantable 
              con la innovación y la excelencia. Cada proyecto ha sido una oportunidad 
              para superar expectativas y establecer nuevos estándares en la industria.
            </Typography>
            <Typography variant="body1" sx={{ lineHeight: 1.8 }}>
              Nuestro crecimiento ha sido sostenido y planificado, siempre priorizando 
              la calidad sobre la cantidad y construyendo relaciones duraderas con 
              nuestros clientes y socios estratégicos.
            </Typography>
          </Paper>
        </Grid>

        <Grid size={{ xs: 12, md: 6 }}>
          <Paper 
            elevation={6} 
            sx={{ 
              p: 4, 
              borderRadius: 4, 
              background: 'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)',
              color: 'white',
              height: '100%', boxSizing: 'border-box',
            }}
          >
            <Typography variant="h5" fontWeight="bold" gutterBottom>
              Hacia el Futuro
            </Typography>
            <Typography variant="body1" sx={{ lineHeight: 1.8, mb: 3 }}>
              Miramos hacia adelante con optimismo y determinación. Nuestros planes 
              incluyen la expansión a nuevos mercados, el desarrollo de tecnologías 
              disruptivas y el fortalecimiento de nuestro equipo humano.
            </Typography>
            <Typography variant="body1" sx={{ lineHeight: 1.8 }}>
              Continuaremos siendo pioneros en soluciones de ingeniería, manteniendo 
              siempre nuestros valores fundamentales y el compromiso con la 
              sostenibilidad y la responsabilidad social.
            </Typography>
          </Paper>
        </Grid>
      </Grid>
    </Container>
  );
};

export default Historia;
